import RouterNavigationTabs from '@/components/organisms/RouterNavigationTabs';
import React from 'react';
import { useUserTabRoutes } from '@/hooks/routes/useUserTabRoutes';
import { Switch } from '@/components/atoms/Switch';
import { computeText } from '@/locales/utils';
import { useIntl } from 'react-intl';
import {
    Container,
    PermissionWrapper,
    PermissionText,
    ButtonWrapper,
    ExpandAllToggle,
    ExpandAllButton,
    Section,
    SectionHeader,
    SectionIcon,
    SectionTitle,
    SectionCount,
    SectionChevron,
    SectionContent,
    Subsection,
    SubsectionTitle,
    FlagsContainer,
} from './AccessFlagsControl.style';

import { Button } from '../../components/atoms/Button/Button';
import useAccessFlagsControlVM from './AccessFlagsControl.viewmodel';

const AccessFlagsControl = () => {
    const { tabRoutes } = useUserTabRoutes();
    const intl = useIntl();
    const {
        handleSave,
        displayedFlags,
        handleSwitch,
        isButtonDisabled,
        isButtonLoading,
        collapsedGroups,
        toggleGroup,
        toggleAllGroups,
        areAllCollapsed,
        filteredGroups,
    } = useAccessFlagsControlVM();

    const renderFlag = (accessFlag: string) => (
        <PermissionWrapper key={accessFlag}>
            <Switch
                onChange={() => {
                    handleSwitch(accessFlag);
                }}
                value={displayedFlags?.[accessFlag] ?? false}
                disabled={
                    (accessFlag === 'MENU_PRODUCT_EDIT_PRICE_ONLY' ||
                        accessFlag === 'MENU_PRODUCT_EDIT_PRICE_BLOCKED_ONLY') &&
                    !displayedFlags?.MENU_PRODUCT_EDIT
                }
            />
            <PermissionText>{computeText(intl, `accessFlags.${accessFlag}`)}</PermissionText>
        </PermissionWrapper>
    );

    return (
        <RouterNavigationTabs routes={tabRoutes}>
            <Container>
                {filteredGroups.length > 0 && (
                    <ExpandAllToggle>
                        <ExpandAllButton onClick={toggleAllGroups}>
                            {areAllCollapsed
                                ? computeText(intl, 'accessFlagsControl.expandAll')
                                : computeText(intl, 'accessFlagsControl.collapseAll')}
                        </ExpandAllButton>
                    </ExpandAllToggle>
                )}

                {filteredGroups.map((group) => {
                    const isCollapsed = collapsedGroups[group.key] ?? false;

                    return (
                        <Section key={group.key}>
                            <SectionHeader isCollapsed={isCollapsed} onClick={() => toggleGroup(group.key)}>
                                <SectionIcon>{group.icon}</SectionIcon>
                                <SectionTitle>{computeText(intl, `accessFlagsGroups.${group.key}`)}</SectionTitle>
                                <SectionCount>
                                    {group.totalCount}{' '}
                                    {group.totalCount > 1
                                        ? computeText(intl, 'accessFlagsControl.permissions')
                                        : computeText(intl, 'accessFlagsControl.permission')}
                                </SectionCount>
                                <SectionChevron isCollapsed={isCollapsed}>▼</SectionChevron>
                            </SectionHeader>

                            <SectionContent isCollapsed={isCollapsed}>
                                {/* Render direct flags */}
                                {group.filteredFlags.length > 0 && (
                                    <FlagsContainer>{group.filteredFlags.map(renderFlag)}</FlagsContainer>
                                )}

                                {/* Render subsections */}
                                {group.filteredSubsections?.map((subsection) => (
                                    <Subsection key={subsection.key}>
                                        <SubsectionTitle>
                                            {computeText(intl, `accessFlagsSubsections.${subsection.key}`)}
                                        </SubsectionTitle>
                                        <FlagsContainer>{subsection.filteredFlags.map(renderFlag)}</FlagsContainer>
                                    </Subsection>
                                ))}
                            </SectionContent>
                        </Section>
                    );
                })}

                <ButtonWrapper>
                    <Button
                        disabled={isButtonDisabled}
                        isLoading={isButtonLoading}
                        fullWidth={false}
                        onClick={handleSave}
                    >
                        {computeText(intl, 'button.save')}
                    </Button>
                </ButtonWrapper>
            </Container>
        </RouterNavigationTabs>
    );
};
export default AccessFlagsControl;
